<style scoped>
.slide {
    position: relative;
    display: none;
    float: left;
    width: 100%;
    margin-right: -100%;
    backface-visibility: hidden;
}
.slide.active,
.slide.target {
    display: block;
}
.slide.to-next.target {
    transform: translateX(100%);
}
.slide.to-prev.target {
    transform: translateX(-100%);
}
.slide.sliding {
    transition: transform 0.3s ease-in-out;
}
.slide.to-next.sliding.active {
    transform: translateX(-100%);
}
.slide.to-prev.sliding.active {
    transform: translateX(100%);
}
.slide.sliding.target {
    transform: translateX(0);
}
</style>
<template>
    <div class="flex relative">
        <button
            v-if="showControl"
            @click="prev()"
            class="p-3 text-primary focus:outline-none"
        >
            <div class="icon w-16 h-16">
                <chevronLeft class="fill-current" />
            </div>
        </button>
        <div class="flex-auto flex justify-center">
            <ul class="relative overflow-hidden w-full">
                <li
                    v-for="(s, i) in slides"
                    :key="s.id"
                    class="slide"
                    :class="slideClass(i)"
                    @transitionend="slideEnd(i)"
                    @click="$emit('clickSlide', i)"
                >
                    <img
                        :src="lazyLoadImage ? s._lazyImgUrl : s.imgUrl"
                        class="max-w-full w-full h-auto block"
                        v-on:error.once="
                            e => {
                                $emit('brokenImgIndex', i);
                                e.currentTarget.src = tinyBlankGifImg;
                                if (activeSlide === i) next();
                            }
                        "
                    />
                </li>
            </ul>
        </div>
        <button
            v-if="showControl"
            @click="next()"
            class="p-3 text-primary focus:outline-none"
        >
            <div class="icon w-16 h-16">
                <chevronRight class="fill-current" />
            </div>
        </button>
        <div v-if="showIndicator" class="absolute bottom-0 left-0 w-full">
            <div class="flex justify-center">
                <button
                    v-for="(s, i) in slides"
                    :key="i"
                    class="p-2 text-gray-300"
                    :class="{ 'text-primary': i === activeSlide }"
                    @click="setSlide(i)"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        class="shadow border-0 rounded-full"
                    >
                        <circle cx="12" cy="12" r="12" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import chevronLeft from '../assets/svg/chevron_left.svg';
import chevronRight from '../assets/svg/chevron_right.svg';
export default {
    components: {
        chevronLeft,
        chevronRight
    },
    props: {
        slides: { type: Array, default: () => [] },
        showControl: { type: Boolean, default: true },
        showIndicator: { type: Boolean, default: true },
        autoPlay: { type: Boolean, default: true },
        brokenImgIdxs: { type: Array, default: () => [] },
        lazyLoadImage: { type: Boolean, default: false },
        preloadAdjacentSlideNum: { type: Number, default: 1 }
    },
    mounted() {
        if (this.autoPlay) {
            this.scheuleNext();
        }
    },
    data() {
        return {
            activeSlide: 0,
            targetSlide: undefined,
            sliding: false,
            direction: '',
            tinyBlankGifImg:
                'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='
        };
    },
    watch: {
        activeSlide: {
            handler(v) {
                this.$emit('updateActiveSlide', v);
                if (this.lazyLoadImage) this.loadActiveSlideAndAdjacentImgs();
            },
            immediate: true
        },
        autoPlay(v) {
            if (v) {
                this.scheuleNext();
            } else {
                if (this.autoPlayTimer) {
                    clearTimeout(this.autoPlayTimer);
                }
            }
        }
    },
    methods: {
        loadActiveSlideAndAdjacentImgs() {
            this.lazyLoadImg(this.activeSlide);
            for (let i = 1; i < this.preloadAdjacentSlideNum + 1; i++) {
                this.lazyLoadImg(this.activeSlide - i);
                this.lazyLoadImg(this.activeSlide + i);
            }
        },
        lazyLoadImg(i) {
            if (this.slides.length === 0) return;

            while (i >= this.slides.length) {
                i = i - this.slides.length;
            }
            while (i < 0) {
                i = i + this.slides.length;
            }

            if (this.slides[i] && !this.slides[i]._lazyImgUrl) {
                this.slides[i]._lazyImgUrl = this.slides[i].imgUrl;
            }
        },
        slideClass(i) {
            const cls = {
                active: this.activeSlide === i,
                target: this.targetSlide === i
            };
            if (cls.active || cls.target) {
                cls.sliding = this.sliding;
                cls[this.direction] = true;
            }
            return cls;
        },
        next() {
            if (this.sliding) {
                return;
            }
            let i = this.activeSlide + 1;

            while (
                i !== this.activeSlide &&
                (i === this.slides.length || this.brokenImgIdxs.includes(i))
            ) {
                if (i === this.slides.length) {
                    i = 0;
                } else {
                    i++;
                }
            }
            this.doSlide(i, 'to-next');
        },
        prev() {
            if (this.sliding) {
                return;
            }
            let i = this.activeSlide - 1;

            while (
                i !== this.activeSlide &&
                (i === -1 || this.brokenImgIdxs.includes(i))
            ) {
                if (i === -1) {
                    i = this.slides.length - 1;
                } else {
                    i--;
                }
            }
            this.doSlide(i, 'to-prev');
        },
        setSlide(i) {
            if (i === this.activeSlide) {
                return;
            }
            this.doSlide(i, i > this.activeSlide ? 'to-next' : 'to-prev');
        },
        doSlide(i, direction) {
            clearTimeout(this.autoPlayTimer);
            this.targetSlide = i;
            this.direction = direction;
            setTimeout(() => (this.sliding = true), 20);
        },
        slideEnd() {
            if (this.targetSlide === undefined) {
                return;
            }
            this.activeSlide = this.targetSlide;
            this.sliding = false;
            this.direction = '';
            this.targetSlide = undefined;
            if (this.autoPlay) {
                this.scheuleNext();
            }
        },
        scheuleNext() {
            if (this.autoPlayTimer) {
                clearTimeout(this.autoPlayTimer);
            }
            this.autoPlayTimer = setTimeout(() => this.next(), 5000);
        }
    }
};
</script>
